<template>
  <v-container class="listening-checkin-container">
    <v-row>
      <v-col cols="12" class="text-center">
        <h1>Check-in na SSP / RRP / NeuroSym</h1>
        <p class="subtitle">
          Een kort moment om stil te staan bij wat je opmerkt na het luisteren. Er is geen goed of fout.
          Ook wanneer je niets bijzonders ervaart, is dat waardevolle informatie.
        </p>
        <v-divider></v-divider>
      </v-col>

      <v-col cols="12">
        <form v-on:submit.prevent="submit">
          <v-row>
            <v-col cols="12" sm="6">
              <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  offset-y
                  min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                      :value="formattedDate"
                      label="Datum"
                      prepend-icon="fa fa-calendar-alt"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                  />
                </template>
                <v-date-picker
                    v-model="form.date"
                    locale="nl-nl"
                    first-day-of-week="1"
                    :max="today"
                    color="accent"
                    @input="dateMenu = false"
                />
              </v-menu>
            </v-col>

            <v-col cols="12" sm="6">
              <div class="field-label">Luisterprogramma</div>
              <v-btn-toggle v-model="form.program" mandatory color="accent" group>
                <v-btn value="ssp">SSP</v-btn>
                <v-btn value="rrp">RRP</v-btn>
                <v-btn value="neurosym">NeuroSym</v-btn>
              </v-btn-toggle>
            </v-col>

            <v-col cols="12" class="pt-0">
              <v-text-field
                  v-model.number="form.minutes"
                  label="Hoeveel minuten heb je geluisterd?"
                  placeholder="Bijvoorbeeld 10 minuten"
                  type="number"
                  min="0"
                  suffix="minuten"
              />
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <h3>1. Hoe voelde je je vóór en na het luisteren?</h3>
          <p class="helper-text">0 = heel onrustig, gespannen of afgesloten · 10 = rustig, veilig en aanwezig</p>

          <div class="slider-label">
            <span>Voor het luisteren</span>
            <strong>{{ form.feeling_before }}/10</strong>
          </div>
          <v-slider
              v-model="form.feeling_before"
              min="0"
              max="10"
              step="1"
              ticks="always"
              tick-size="3"
              color="accent"
              track-color="grey darken-1"
          />

          <div class="slider-label">
            <span>Na het luisteren</span>
            <strong>{{ form.feeling_after }}/10</strong>
          </div>
          <v-slider
              v-model="form.feeling_after"
              min="0"
              max="10"
              step="1"
              ticks="always"
              tick-size="3"
              color="accent"
              track-color="grey darken-1"
          />

          <v-divider class="my-4"></v-divider>

          <h3>2. Wat merk je op in je lichaam ná het luisteren?</h3>
          <p class="helper-text">Meerdere antwoorden mogelijk.</p>
          <v-row no-gutters>
            <v-col cols="12" sm="6" v-for="option in options.body" :key="option.value">
              <v-checkbox
                  v-model="form.body"
                  :value="option.value"
                  :label="option.label"
                  color="accent"
                  hide-details
                  dense
              />
            </v-col>
          </v-row>

          <v-divider class="my-6"></v-divider>

          <h3>3. Hoe voel je je emotioneel ná het luisteren?</h3>
          <p class="helper-text">Meerdere antwoorden mogelijk.</p>
          <v-row no-gutters>
            <v-col cols="12" sm="6" v-for="option in options.emotional" :key="option.value">
              <v-checkbox
                  v-model="form.emotional"
                  :value="option.value"
                  :label="option.label"
                  color="accent"
                  hide-details
                  dense
              />
            </v-col>
          </v-row>

          <v-divider class="my-6"></v-divider>

          <h3>4. Wat viel je op tijdens of na het luisteren?</h3>
          <v-textarea
              v-model="form.noticed"
              placeholder="Bijvoorbeeld gedachten, gevoelens, lichamelijke reacties of niets bijzonders."
              rows="3"
              auto-grow
              outlined
              class="mt-2"
          />

          <h3>5. Wat heb je nu nodig?</h3>
          <v-textarea
              v-model="form.needs"
              placeholder="Bijvoorbeeld rust, beweging, contact, iets drinken of even niets."
              rows="3"
              auto-grow
              outlined
              class="mt-2"
          />

          <v-btn color="accent" block height="52" type="submit" :loading="loading" :disabled="loading">
            Opslaan
          </v-btn>
        </form>
      </v-col>

      <v-col cols="12">
        <p class="helper-text">
          <strong>Voor de deelnemer:</strong> Je hoeft niets te veranderen of te verklaren. Merk alleen op wat er is.
          Ervaar je duidelijke overbelasting of aanhoudende klachten, bespreek dit dan met je begeleider voordat je
          verder luistert.
        </p>
      </v-col>

      <v-col cols="12" v-if="checkins.length > 0">
        <v-divider class="mb-4"></v-divider>
        <h3 class="mb-2">Eerdere check-ins</h3>
        <v-simple-table dense>
          <thead>
          <tr>
            <th>Datum</th>
            <th>Programma</th>
            <th>Minuten</th>
            <th>Voor → na</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="checkin in checkins" :key="checkin.id">
            <td>{{ formatDate(checkin.date) }}</td>
            <td>{{ programLabels[checkin.program] || checkin.program }}</td>
            <td>{{ checkin.minutes }}</td>
            <td>{{ checkin.feeling_before }} → {{ checkin.feeling_after }}</td>
          </tr>
          </tbody>
        </v-simple-table>
      </v-col>
    </v-row>

    <snackbar-text :text="error.message" bg-color="red" text-color="white" v-model="error.show"/>
    <snackbar-text :text="success.message" bg-color="green" text-color="white" v-model="success.show" :button="false" :timeout="4000"/>
  </v-container>
</template>

<script>
import apiClient from '@/services/apiClient';
import SnackbarText from '@/components/snackbar-text';

const PROGRAM_LABELS = {
  ssp: 'SSP',
  rrp: 'RRP',
  neurosym: 'NeuroSym',
};

const DEFAULT_OPTIONS = {
  body: [
    {value: 'more_relaxation', label: 'Meer ontspanning'},
    {value: 'more_tension', label: 'Meer spanning'},
    {value: 'warmth_tingling', label: 'Warmte of tintelingen'},
    {value: 'changed_breathing', label: 'Veranderde ademhaling'},
    {value: 'fatigue', label: 'Vermoeidheid'},
    {value: 'more_energy', label: 'Meer energie'},
    {value: 'headache_dizziness', label: 'Hoofdpijn of duizeligheid'},
    {value: 'no_difference', label: 'Geen verschil'},
  ],
  emotional: [
    {value: 'calm_relaxed', label: 'Rustig / ontspannen'},
    {value: 'happy_connected', label: 'Blij / verbonden'},
    {value: 'sad_touched', label: 'Verdrietig / geraakt'},
    {value: 'restless_anxious', label: 'Onrustig / angstig'},
    {value: 'irritated', label: 'Geïrriteerd'},
    {value: 'flat_withdrawn', label: 'Vlak / teruggetrokken'},
    {value: 'overstimulated', label: 'Overprikkeld'},
    {value: 'neutral', label: 'Neutraal'},
  ],
};

function todayString() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function emptyForm() {
  return {
    date: todayString(),
    program: 'ssp',
    minutes: null,
    feeling_before: 5,
    feeling_after: 5,
    body: [],
    emotional: [],
    noticed: '',
    needs: '',
  };
}

// Accepts either {key: label} or [{value, label}] from the options endpoint
function normalizeOptions(raw, fallback) {
  if (Array.isArray(raw)) {
    return raw.map((option) => ({
      value: option.value || option.key,
      label: option.label || option.value || option.key,
    }));
  }
  if (raw && typeof raw === 'object') {
    return Object.keys(raw).map((key) => ({value: key, label: raw[key]}));
  }

  return fallback;
}

export default {
  name: 'listeningCheckin',
  components: {SnackbarText},
  data: () => ({
    form: emptyForm(),
    options: DEFAULT_OPTIONS,
    programLabels: PROGRAM_LABELS,
    checkins: [],
    today: todayString(),
    dateMenu: false,
    loading: false,
    error: {
      show: false,
      message: '',
    },
    success: {
      show: false,
      message: 'Je check-in is opgeslagen. Dank je wel!',
    },
  }),
  computed: {
    formattedDate() {
      return this.formatDate(this.form.date);
    },
  },
  mounted() {
    this.loadOptions();
    this.loadCheckins();
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      const [year, month, day] = date.substring(0, 10).split('-');

      return `${day}-${month}-${year}`;
    },
    loadOptions() {
      apiClient.get('listening-checkin/options').then((response) => {
        const data = response.data && response.data.data ? response.data.data : response.data;
        if (!data) return;

        this.options = {
          body: normalizeOptions(data.body, DEFAULT_OPTIONS.body),
          emotional: normalizeOptions(data.emotional, DEFAULT_OPTIONS.emotional),
        };
      }).catch((err) => {
        console.error('Error loading listening checkin options:', err);
      });
    },
    loadCheckins() {
      apiClient.get('listening-checkin').then((response) => {
        const data = response.data && response.data.data ? response.data.data : response.data;
        this.checkins = Array.isArray(data) ? data : [];
      }).catch((err) => {
        console.error('Error loading listening checkins:', err);
      });
    },
    getFormData() {
      const formData = {
        date: this.form.date,
        program: this.form.program,
        feeling_before: this.form.feeling_before,
        feeling_after: this.form.feeling_after,
        body: this.form.body,
        emotional: this.form.emotional,
      };

      if (this.form.minutes !== null && this.form.minutes !== '') {
        formData.minutes = this.form.minutes;
      }
      if (this.form.noticed) {
        formData.noticed = this.form.noticed;
      }
      if (this.form.needs) {
        formData.needs = this.form.needs;
      }

      return formData;
    },
    submit() {
      this.loading = true;

      apiClient.post('listening-checkin', this.getFormData()).then(() => {
        this.success.show = true;
        this.loading = false;
        this.form = emptyForm();
        this.loadCheckins();
        window.scrollTo({top: 0, behavior: 'smooth'});
      }).catch((err) => {
        this.error.message = 'Er is een fout opgetreden bij het opslaan. Probeer het later opnieuw.';
        this.error.show = true;
        this.loading = false;
        console.error('Error submitting listening checkin:', err);
      });
    },
  },
};
</script>

<style scoped>
.listening-checkin-container {
  max-width: 760px;
  margin: 0 auto;
  padding-bottom: 40px;
}

h1 {
  font-size: 28px;
  font-weight: bold;
}

.subtitle {
  font-size: 16px;
  color: #E0E0E0;
}

.helper-text {
  font-size: 14px;
  color: #D0D0D0;
}

.field-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
}

.slider-label {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 8px;
}

.slider-label strong {
  font-size: 20px;
}
</style>
